/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, Input, ViewEncapsulation, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ThaiDatePipe } from '@alfresco/aca-shared';
import { TranslatePipe } from '@ngx-translate/core';
import { DmsMetadataService } from '../upload/dms-metadata.service';

/**
 * Document-list cell for a `dms:*` metadata property (column key
 * `properties.dms:…`). Constrained values — document type, status,
 * confidentiality — are resolved through {@link DmsMetadataService.label} so a
 * stored code renders as its configured label, and `dms:documentDate` renders
 * as a Buddhist-Era date like the rest of the product. Anything else shows its
 * raw value, which is also the honest answer for a field added to the model
 * after this map was written.
 *
 * Registered as `app.columns.dms-field`; one component serves every dms column.
 */
@Component({
  selector: 'app-dms-field-column',
  standalone: true,
  imports: [CommonModule, ThaiDatePipe, TranslatePipe],
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `{{ date ? (value | acaDate: 'd MMM y') || '—' : (display | translate) }}`,
  host: { class: 'app-dms-field-column' }
})
export class DmsFieldColumnComponent {
  constructor() {
    // warms the shared schema/label cache so LIST values added from the
    // Metadata fields screen render their configured labels, not raw codes
    inject(DmsMetadataService)
      .schema()
      .subscribe({ error: () => undefined });
  }

  value: string | null = null;
  display = '—';
  date = false;

  /** Date-typed dms properties — rendered through the Thai date pipe, not as raw ISO text. */
  private static readonly DATE_FIELDS = new Set(['dms:documentDate']);

  @Input()
  set context(ctx: any) {
    const entry = ctx?.row?.node?.entry;
    const key: string = ctx?.col?.key ?? '';
    const raw = key.split('.').reduce((node: any, part: string) => node?.[part], entry);

    this.date = DmsFieldColumnComponent.DATE_FIELDS.has(key);
    this.value = raw === null || raw === undefined || raw === '' ? null : String(raw);
    this.display = this.value === null ? '—' : DmsMetadataService.label(this.value);
  }
}
